import React, { useEffect, useState } from 'react'
import {
  Typography,
  Button,
  FormControl,
  Box,
  Select,
  InputLabel,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  Stack,
  Alert,
} from '@mui/material'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import LocalBarIcon from '@mui/icons-material/LocalBar'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '../context/authcontext'
import SquareRadioButtons from '../components/SquareRadioButtons'
import { useGetEventModes, useGetControlPoints } from '../hooks/events'
import { ScanModes } from '../utils/scanModeEnum'
import { useScan } from '../context/scanContext'
import AppShell from '../components/layout/AppShell'
import { SectionTitle } from '../components/ui/Surface'

const EventModeView = () => {
  const {
    scanMode,
    setScanMode,
    controlPoint,
    setControlPoint,
    products,
    setProducts,
    saveScanConfig,
    isConfigured,
  } = useScan()
  const { eventModes } = useGetEventModes()
  const { controlPoints } = useGetControlPoints()
  const { user } = useAuth()

  const navigate = useNavigate()

  const [error, setError] = useState()

  useEffect(() => {
    if (!user.Admin && isConfigured) {
      navigate('/scan')
    }
  }, [])

  const handleChange = (event) => {
    const {
      target: { value },
    } = event
    if (typeof value === 'string') {
      setProducts(value.split(','))
    } else {
      // Créer un nouveau tableau basé sur les options sélectionnées
      const selectedOptions = value.map((val) => {
        return eventModes.Options.find((option) => JSON.parse(option.Name).fr === val)
      })
      setProducts(selectedOptions)
    }
  }

  const handleSelect = () => {
    setError('')
    if (scanMode === ScanModes.PRODUCT && products.length === 0) {
      setError('Veuillez sélectionner au moins un produit')
      return
    }

    if (scanMode === ScanModes.ACCESS && !controlPoint) {
      setError('Veuillez sélectionner au moins un point de contrôle')
      return
    }

    if (saveScanConfig()) {
      navigate('/scan')
    }
  }

  const options = eventModes?.Options || []
  const availableControlPoints = controlPoints || []

  return (
    <AppShell
      footer={
        <Button
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSelect}
        >
          Valider la configuration
        </Button>
      }
    >
      <Stack spacing={3} sx={{ flexGrow: 1 }}>
        <SectionTitle
          overline="Configuration"
          title="Sélection du mode"
          description="Choisissez ce que cet appareil doit contrôler pendant l'événement."
        />

        <SquareRadioButtons
          valueLeft={ScanModes.ACCESS}
          valueRight={ScanModes.PRODUCT}
          valueLeftLabel="Contrôle d'accès"
          valueRightLabel="Consommation de produits"
          valueLeftIcon={<MeetingRoomIcon />}
          valueRightIcon={<LocalBarIcon />}
          onChange={setScanMode}
          value={scanMode}
        />

        {scanMode === ScanModes.ACCESS ? (
          <FormControl fullWidth>
            <InputLabel id="control-point-label">Point de contrôle</InputLabel>
            <Select
              labelId="control-point-label"
              label="Point de contrôle"
              value={controlPoint || ''}
              onChange={(selection) => {
                const value = selection.target.value
                setControlPoint(value)
                setProducts(value.Products)
              }}
            >
              {availableControlPoints.map((option) => (
                <MenuItem key={option.Id ?? option.Name} value={option}>
                  {option.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : scanMode === ScanModes.PRODUCT ? (
          <FormControl fullWidth>
            <InputLabel id="products-label">Produits</InputLabel>
            <Select
              labelId="products-label"
              multiple
              value={products.map((p) => JSON.parse(p.Name).fr) || []}
              onChange={handleChange}
              input={<OutlinedInput label="Produits" />}
              renderValue={(selected) =>
                selected.length > 1 ? `${selected.length} produits sélectionnés` : selected
              }
            >
              {options.map((option) => {
                const optionName = JSON.parse(option.Name).fr
                return (
                  <MenuItem key={option.Id} value={optionName}>
                    <Checkbox checked={products.some((product) => product.Id === option.Id)} />
                    <ListItemText primary={optionName} />
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Sélectionnez un mode pour poursuivre la configuration.
          </Typography>
        )}

        {error ? <Alert severity="warning">{error}</Alert> : null}

        <Box sx={{ flexGrow: 1 }} />
      </Stack>
    </AppShell>
  )
}

export default EventModeView
